import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { fetchAdminMe, fetchSmsStatus, fetchAtLog, fetchEmailStatus } from "@/lib/api";
import { MessageSquareText, Loader2, ArrowLeft, CheckCircle2, AlertTriangle, RefreshCw, XCircle, Mail } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "medium" }) : "—");

export default function AdminSmsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState(null);
  const [email, setEmail] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const [s, l, e] = await Promise.all([fetchSmsStatus(), fetchAtLog(50), fetchEmailStatus()]);
      setStatus(s);
      setLog(l);
      setEmail(e);
    } catch {
      toast.error("Erreur de chargement du diagnostic SMS");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAdminMe().then(load).catch(() => navigate("/", { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  if (!status || !log) {
    return <main className="max-w-7xl mx-auto px-6 py-20 text-center"><Loader2 className="animate-spin mx-auto text-orange-500" /></main>;
  }

  return (
    <main className="max-w-6xl mx-auto px-6 py-10" data-testid="admin-sms-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-orange-600 font-semibold mb-4" data-testid="back-to-admin">
        <ArrowLeft size={14} /> Tableau de bord
      </Link>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <h1 className="font-display text-4xl font-extrabold tracking-tight inline-flex items-center gap-3">
          <MessageSquareText size={32} className="text-orange-500" /> SMS &amp; Emails
        </h1>
        <button onClick={load} disabled={refreshing} className="btn-secondary inline-flex items-center gap-2" data-testid="sms-refresh-btn">
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Actualiser
        </button>
      </div>

      <SmsStatusCard s={status} />
      {email && <EmailStatusCard e={email} />}

      <h2 className="text-lg font-bold mt-8 mb-3">Derniers envois ({log.count})</h2>
      {log.count === 0 ? (
        <p className="text-sm text-[var(--ink-soft)]" data-testid="sms-log-empty">
          Aucun envoi enregistré depuis le dernier redémarrage du serveur.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid="sms-log-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-[var(--ink-soft)] border-b border-[var(--line)]">
                <th className="py-2 pr-3">Date</th>
                <th className="py-2 pr-3">Numéro</th>
                <th className="py-2 pr-3">Expéditeur</th>
                <th className="py-2 pr-3">Statut envoi</th>
                <th className="py-2 pr-3">Livraison</th>
                <th className="py-2 pr-3">Code</th>
                <th className="py-2 pr-3">Coût</th>
                <th className="py-2 pr-3">Détail</th>
              </tr>
            </thead>
            <tbody>
              {log.entries.map((e, i) => {
                const ok = String(e.status).toLowerCase() === "success";
                return (
                  <tr key={i} className="border-b border-[var(--line)]/60" data-testid={`sms-log-row-${i}`}>
                    <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(e.at_utc)}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{e.phone}</td>
                    <td className="py-2 pr-3 text-xs">{({ termii: "Termii", smsala: "SMSala" }[e.raw?.provider] || "AT")} · {e.raw?.sender || "—"}</td>
                    <td className="py-2 pr-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                        {ok ? <CheckCircle2 size={12} /> : <XCircle size={12} />} {e.status}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-xs" data-testid={`sms-log-dlr-${i}`}>
                      {e.dlr ? (
                        <span className={`px-2 py-0.5 rounded-full font-bold ${e.dlr === "Delivered" ? "bg-emerald-50 text-emerald-700" : /Rejected|Undeliverable|Expired|Deleted/.test(e.dlr) ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`} title={e.dlr_source === "webhook" ? "Reçu par webhook SMSala" : "Interrogé via l'API SMSala"}>
                          {e.dlr}{e.dlr_type ? ` · ${e.dlr_type}` : ""}{e.dlr_source === "webhook" ? " ⚡" : ""}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="py-2 pr-3">{e.statusCode ?? "—"}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{e.cost || "—"}</td>
                    <td className="py-2 pr-3 text-xs text-[var(--ink-soft)] max-w-md break-all">{e.exception || (ok ? e.messageId : JSON.stringify(e.raw))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}

function SmsStatusCard({ s }) {
  const problems = [];
  if (!s.sms_enabled) problems.push(s.disabled_reason || "Service SMS désactivé sur ce serveur");
  if (s.is_sandbox_username) problems.push("Username 'sandbox' : les SMS ne sortent jamais vers de vrais numéros");
  if (!s.sender_id) problems.push("AT_SENDER_ID absent : envoi avec l'expéditeur par défaut, souvent bloqué par Orange/MTN CI");
  if (s.balance && /XOF\s*0(\.|$)/.test(s.balance)) problems.push("Solde Africa's Talking épuisé");
  if (s.smsala_enabled && !s.smsala_via_proxy) problems.push("SMSala : aucun proxy IP fixe configuré — SMSala refuse les IP non autorisées (« Ip Address Not Allowed »).");
  if (s.smsala_enabled && !s.smsala_sender_approved) problems.push(`SMSala : le Sender ID ${s.smsala_sender_id} n'est pas encore approuvé (statut « Requested ») — les OTP partent avec l'expéditeur « ${s.smsala_effective_sender} » en attendant. Demandez l'approbation dans le dashboard SMSala (Sender ID → Cote d'Ivoire, type Transactional/OTP).`);
  if (s.termii_enabled && !s.termii_sender_approved) problems.push("Termii : le Sender ID FODINDIN n'est pas encore approuvé — demandez-le sur app.termii.ai (Sender ID → Request) ; en attendant Termii peut rejeter les envois.");
  if (s.dev_whitelist_count > 0) problems.push(`${s.dev_whitelist_count} numéro(s) en liste blanche DEV (${(s.dev_whitelist_masked || []).join(", ")}) : ces numéros ne reçoivent JAMAIS de SMS, le code n'apparaît que dans les journaux serveur. En production, mettez le secret DEV_OTP_WHITELIST à 0.`);
  const healthy = problems.length === 0;
  return (
    <div className="glass-card p-5 mt-6" data-testid="sms-status-card">
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase border ${s.sms_enabled ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-red-50 text-red-800 border-red-300"}`} data-testid="sms-mode">
          {s.sms_enabled ? "SMS réels activés" : "Mode dryrun (aucun SMS envoyé)"}
        </span>
        <span className="text-xs text-[var(--ink-soft)]">Serveur : <b>{s.environment_hint}</b></span>
      </div>
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm mt-4">
        <Row k="Username AT" v={s.username} />
        <Row k="Clé API" v={s.api_key_set ? s.api_key_hint || "définie" : "ABSENTE"} bad={!s.api_key_set} />
        <Row k="Sender ID" v={s.sender_id || "ABSENT"} bad={!s.sender_id} />
        <Row k="OTP_SECRET" v={s.otp_secret_set ? "défini" : "ABSENT (valeur par défaut)"} bad={!s.otp_secret_set} />
        <Row k="Solde" v={s.balance || s.balance_error || "—"} testid="sms-balance" />
        <Row k="Numéros whitelist dev" v={s.dev_whitelist_count ? (s.dev_whitelist_masked || []).join(", ") : "aucun"} bad={s.dev_whitelist_count > 0} testid="sms-whitelist" />
        <Row k="Canal principal : SMSala" v={s.smsala_enabled ? `actif · expéditeur ${s.smsala_effective_sender || s.smsala_sender_id}${s.smsala_via_proxy ? " · via proxy IP fixe" : " · SANS proxy (IP à autoriser)"}` : "non configuré (SMSALA_API_TOKEN)"} bad={!s.smsala_enabled || !s.smsala_via_proxy} testid="sms-smsala" />
        <Row k="Sender ID SMSala" v={s.smsala_enabled ? (s.smsala_sender_approved ? `${s.smsala_sender_id} approuvé` : `${s.smsala_sender_id} en attente d'approbation → envoi via « ${s.smsala_effective_sender} » (${(s.smsala_senders || []).map((x) => `${x[0]}:${x[2]}`).join(", ") || "liste indisponible"})`) : "—"} bad={s.smsala_enabled && !s.smsala_sender_approved} testid="sms-smsala-sender" />
        <Row k="Webhook DLR SMSala" v={s.smsala_enabled ? s.smsala_dlr_url || "—" : "—"} testid="sms-smsala-dlr-url" />
        <Row k="Canal 2 : Termii" v={s.termii_enabled ? `actif · expéditeur ${s.termii_sender_id} · solde ${s.termii_balance || "—"}` : "non configuré (TERMII_API_KEY)"} bad={!s.termii_enabled} testid="sms-termii" />
        <Row k="Sender ID Termii" v={s.termii_enabled ? (s.termii_sender_approved ? "approuvé" : `en attente d'approbation (${(s.termii_sender_ids || []).map((x) => x.join(":")).join(", ") || "aucune demande"})`) : "—"} bad={s.termii_enabled && !s.termii_sender_approved} testid="sms-termii-sender" />
        <Row k="Ordre d'envoi" v={(s.provider_order || []).map((p) => ({ smsala: "SMSala", at: "Africa's Talking", termii: "Termii" }[p] || p)).join(" → ")} testid="sms-order" />
      </dl>
      <div className="mt-4" data-testid="sms-diagnosis">
        {healthy ? (
          <p className="inline-flex items-center gap-2 text-emerald-700 font-semibold text-sm"><CheckCircle2 size={16} /> Configuration complète — si un SMS n&apos;arrive pas, consultez le statut AT ligne par ligne ci-dessous.</p>
        ) : (
          <ul className="space-y-1">
            {problems.map((p) => (
              <li key={p} className="inline-flex items-start gap-2 text-red-700 font-semibold text-sm"><AlertTriangle size={16} className="shrink-0 mt-0.5" /> {p}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function EmailStatusCard({ e }) {
  const STATUS_STYLE = { sent: "bg-emerald-50 text-emerald-700", failed: "bg-red-50 text-red-700", dryrun: "bg-gray-100 text-gray-600" };
  return (
    <div className="glass-card p-5 mt-6" data-testid="email-status-card">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="inline-flex items-center gap-2 font-bold"><Mail size={18} className="text-orange-500" /> Emails sortants (Resend)</span>
        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase border ${e.email_enabled && !e.sandbox_sender ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-red-50 text-red-800 border-red-300"}`} data-testid="email-mode">
          {!e.email_enabled ? "Dryrun (aucun email envoyé)" : e.sandbox_sender ? "Sandbox Resend" : "Actif"}
        </span>
      </div>
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm mt-4">
        <Row k="Clé Resend" v={e.api_key_set ? e.api_key_hint || "définie" : "ABSENTE"} bad={!e.api_key_set} />
        <Row k="Expéditeur" v={e.sender_email} bad={e.sandbox_sender} />
      </dl>
      {e.warning && (
        <p className="mt-3 inline-flex items-start gap-2 text-red-700 font-semibold text-sm" data-testid="email-warning"><AlertTriangle size={16} className="shrink-0 mt-0.5" /> {e.warning}</p>
      )}
      {e.recent_contact_requests?.length > 0 && (
        <div className="mt-4">
          <div className="text-xs uppercase tracking-wider text-[var(--ink-soft)] mb-2">Derniers messages du formulaire contact</div>
          <ul className="space-y-1 text-sm" data-testid="contact-requests-list">
            {e.recent_contact_requests.map((r, i) => (
              <li key={i} className="flex items-center gap-2 flex-wrap">
                <span className="text-[var(--ink-soft)] whitespace-nowrap">{fmtDate(r.created_at)}</span>
                <span className="font-mono text-xs">{r.email}</span>
                <span className="text-[var(--ink-soft)]">→ {r.to_email}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${STATUS_STYLE[r.email_status] || "bg-gray-100 text-gray-500"}`}>{r.email_status || "non transmis"}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Row({ k, v, bad, testid }) {
  return (
    <div className="flex gap-2 min-w-0" data-testid={testid}>
      <dt className="text-[var(--ink-soft)] whitespace-nowrap">{k} :</dt>
      <dd className={`font-medium truncate ${bad ? "text-red-700" : ""}`} title={String(v)}>{v}</dd>
    </div>
  );
}
